import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please complete name, email and message.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      });
      setSent(true);
      toast.success('Thank you — your note has reached the studio.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-bg">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-40 grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-5 reveal">
          <span className="kicker">Let’s talk</span>
          <h2 className="font-display text-ink mt-3 leading-[0.95] tracking-tight text-[clamp(2.8rem,7vw,6rem)]">
            Start a<br />
            <span className="italic text-crimson">conversation</span>.
          </h2>
          <p className="mt-6 max-w-md text-sub leading-relaxed">
            New project, a glass commission, or simply a coffee in Bristol — the studio reads
            every message.
          </p>
          <div className="mt-12 space-y-5 text-sm">
            <div>
              <div className="kicker">Studio</div>
              <div className="font-display text-xl text-ink mt-1">Bristol, United Kingdom</div>
            </div>
            <div>
              <div className="kicker">Email</div>
              <a
                href="mailto:hello@bedeviandesigns.com"
                data-testid="email-link"
                className="font-display text-xl text-ink hover-underline mt-1 inline-block"
              >
                hello@bedeviandesigns.com
              </a>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-7 reveal">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7"
          >
            <Field
              label="Your name"
              value={form.name}
              onChange={update('name')}
              testid="contact-name"
              required
            />
            <Field
              label="Email"
              type="email"
              value={form.email}
              onChange={update('email')}
              testid="contact-email"
              required
            />
            <div className="md:col-span-2">
              <Field
                label="Subject (optional)"
                value={form.subject}
                onChange={update('subject')}
                testid="contact-subject"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block">
                <span className="kicker">Message</span>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={update('message')}
                  rows={5}
                  required
                  className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-ink font-sans text-base resize-none transition-colors"
                  placeholder="Tell me about your project, space or idea…"
                />
              </label>
            </div>

            <div className="md:col-span-2 mt-2 flex flex-wrap items-center justify-between gap-4">
              <p className="text-xs text-sub max-w-md leading-relaxed">
                By submitting you consent to the studio storing your message for the purpose of
                responding to your enquiry.
              </p>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="btn-primary disabled:opacity-60"
              >
                {loading ? 'Sending…' : sent ? 'Sent ✓' : 'Send message'}
                {!loading && !sent && <span aria-hidden>→</span>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = 'text', testid, required }) {
  return (
    <label className="block">
      <span className="kicker">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-ink font-sans text-base transition-colors"
      />
    </label>
  );
}
